import { Icon } from '@iconify/react';
import { SortDescriptor, State } from '@progress/kendo-data-query';
import { FieldWrapper } from '@progress/kendo-react-form';
import { InputPrefix, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { get } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CatalogTreeViewDropdown from 'src/components/design-system/CatalogTreeViewDropdown';
import TreeViewCheckbox from 'src/components/design-system/TreeViewCheckbox';
import SupplierDropdown from 'src/sections/catalog_products/SupplierDropdown';
import { useContextSelector } from 'use-context-selector';
import {
  initialListViewDataState,
  initialPictureViewDataState,
  PRESERVE_SORT_FIELDS,
  ViewCatalogProductsContext,
} from './ViewCatalogProductsContext';

const ManageProductFilters = () => {
  const { t } = useTranslation();
  // Fetch catalogs from API
  const catalogs = useContextSelector(ViewCatalogProductsContext, (context) => context.catalogs);
  const categories = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.categories
  );

  const selectedCatalog = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedCatalog
  );

  const handleCatalogChange = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleCatalogChange
  );
  const handleCategoryChange = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleCategoryChange
  );

  const categoryDropdownPlaceholder = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.categoryDropdownPlaceholder
  );
  const catalogDropdownPlaceholder = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.catalogDropdownPlaceholder
  );

  const selectedView = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedView
  );
  const setSelectedView = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setSelectedView
  );

  const search = useContextSelector(ViewCatalogProductsContext, (context) => context.search);
  const handleSearchChange = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleSearchChange
  );
  const setDataState = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setDataState
  );

  // Offline suppliers state
  const isOfflineCatalog = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isOfflineCatalog
  );
  const supplierData = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.supplierData
  );
  const handleSupplierChange = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleSupplierChange
  );

  const isLoadingSuppliers = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isLoadingSuppliers
  );

  const handleConfirm = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleConfirm
  );
  const setProductSelections = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setProductSelections
  );

  const dataState = useContextSelector(ViewCatalogProductsContext, (context) => context.dataState);
  const products = useContextSelector(ViewCatalogProductsContext, (context) => context.products);
  const getSelectedItems = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.getSelectedItems
  );
  const getTakeForView = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.getTakeForView
  );

  const shouldPreserveSort = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.shouldPreserveSort
  );

  const shouldShowUnsavedModal = useCallback(() => {
    const selectedItems = getSelectedItems();
    const isFirstPage = dataState.skip === 0 || dataState.skip === undefined;
    const isWithinRange = dataState.take === 10 || dataState.take === 25;
    if (selectedItems.length === 0) {
      return false;
    }
    // Get indexes of selected products
    const selectedProductIndexes = products.reduce((indexes: number[], product, index) => {
      if (selectedItems.some((item) => item.productKey === product.productKey)) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    const isSelectedItemsIndexWithinRange = selectedProductIndexes.every((index) => index <= 24);

    // Don't show modal if:
    // 1. First page and within range (10 or 25 items)
    // 2. First page, not within range, but selected items are within first 25
    if (isFirstPage && isWithinRange) {
      return false;
    }
    if (isFirstPage && !isWithinRange && isSelectedItemsIndexWithinRange) {
      return false;
    }

    return true;
  }, [getSelectedItems, dataState, products]);

  const handleTabChange = useCallback(
    async (e: TabStripSelectEventArguments): Promise<void> => {
      // Get the stored take value for the target view (or use default if not set)
      const targetTake = getTakeForView(e.selected);
      const targetView = e.selected;

      const updateViewAndDataState = () => {
        setSelectedView(targetView);
        setDataState((prev: State): State => {
          const sort = get(prev, 'sort', []);
          const defaultSort =
            targetView === 0 ? initialListViewDataState.sort : initialPictureViewDataState.sort;
          const shouldPreserve = shouldPreserveSort(sort);
          return {
            ...prev,
            take: targetTake,
            skip: 0,
            sort: shouldPreserve
              ? sort.filter((s: SortDescriptor) => PRESERVE_SORT_FIELDS.includes(s.field))
              : defaultSort,
          };
        });
      };

      if (shouldShowUnsavedModal()) {
        await handleConfirm(() => {
          setProductSelections({});
          updateViewAndDataState();
        });
      } else {
        updateViewAndDataState();
      }
    },
    [
      getTakeForView,
      shouldShowUnsavedModal,
      handleConfirm,
      setProductSelections,
      setSelectedView,
      setDataState,
      shouldPreserveSort,
    ]
  );

  return (
    <div className="flex items-center justify-start gap-4 mt-4 mb-2 flex-wrap px-4 w-full">
      {/* Tab Strip for List/Grid View */}
      <TabStrip
        className="min-w-[190px] flex-shrink-0"
        selected={selectedView}
        onSelect={handleTabChange}
      >
        <TabStripTab contentClassName="!hidden" title={t('List View')} />
        <TabStripTab contentClassName="!hidden" title={t('Picture View')} />
      </TabStrip>

      {/* Catalog Dropdown */}
      <div className="flex items-center gap-2 flex-[1_1_250px] min-w-[200px] max-w-[350px]">
        <span className="text-xs flex items-center whitespace-nowrap flex-shrink-0">
          {t('Select Catalog')}
        </span>
        <div className="flex-1 min-w-0">
          <CatalogTreeViewDropdown
            data={catalogs || []}
            onChange={handleCatalogChange}
            value={selectedCatalog}
            placeholder={catalogDropdownPlaceholder}
            width="100%"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-[1_1_250px] min-w-[200px] max-w-[350px]">
        <span className="text-xs flex items-center whitespace-nowrap flex-shrink-0">
          {isOfflineCatalog ? t('Select Supplier') : t('Select Product Category')}
        </span>
        <div className="flex-1 min-w-0">
          {isOfflineCatalog ? (
            <SupplierDropdown
              data={supplierData}
              onChange={handleSupplierChange}
              isLoading={isLoadingSuppliers}
            />
          ) : (
            <TreeViewCheckbox
              data={categories || []}
              placeholder={categoryDropdownPlaceholder}
              width="100%"
              onChange={handleCategoryChange}
            />
          )}
        </div>
      </div>

      {/* Search Box */}
      <div className="flex-[1_1_250px] min-w-[200px] max-w-[350px]">
        <FieldWrapper className="flex flex-row space-x-2">
          <Label editorId={'search-input'} className="text-xs flex items-center whitespace-nowrap">
            {t('Filter By')}
          </Label>
          <TextBox
            id={'search-input'}
            max={500}
            maxLength={500}
            size="medium"
            style={{
              fontSize: '14px',
            }}
            placeholder={t('Enter Item # or Description')}
            width="100%"
            value={search}
            onChange={async (e: TextBoxChangeEvent): Promise<void> => {
              await handleSearchChange(e.value as string);
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            prefix={() => (
              <InputPrefix className="pl-2">
                <Icon height={'18'} width={'18'} color="#AAAAAA" icon={'lucide:search'} />
              </InputPrefix>
            )}
          />
        </FieldWrapper>
      </div>
    </div>
  );
};

export default ManageProductFilters;
